import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, ImageOff, Printer, Star, StickyNote, Trash2, Upload } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import {
  createAttachment,
  createNote,
  deleteAttachment,
  fetchAttachmentBlobUrl,
  getTechCard,
  listAttachments,
  listNotes,
  updateAttachment,
  uploadAttachment,
  type AttachmentView,
  type PdevMeta,
} from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AuthImage } from "@/pages/product-dev/AuthImage";
import { PEREDEL_LABEL, PEREDEL_STYLES, apiErr, fmtDate, money, num } from "@/pages/product-dev/format";

const selectCls =
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

const isImg = (url: string | null): boolean =>
  !!url && /\.(png|jpe?g|gif|webp|avif)(\?|$)/i.test(url);

// ── Вложения (фото / макеты; файл|внешняя ссылка) ─────────────────────────────
export function AttachmentsPanel({
  projectId,
  meta,
}: {
  projectId: string;
  meta: PdevMeta;
}): React.ReactElement {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["pdev", "attachments", projectId],
    queryFn: () => listAttachments(projectId),
  });
  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["pdev", "attachments", projectId] });
    qc.invalidateQueries({ queryKey: ["pdev", "project", projectId] });
  };

  const [kind, setKind] = useState("photo");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createAttachment(projectId, { kind, title: title.trim() || null, url: url.trim() }),
    onSuccess: () => {
      setTitle("");
      setUrl("");
      invalidate();
      toast.success("Добавлено");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось добавить")),
  });
  const upload = useMutation({
    mutationFn: (f: File) => uploadAttachment(projectId, f, { kind, title: title.trim() || undefined }),
    onSuccess: () => {
      setTitle("");
      invalidate();
      toast.success("Файл загружен");
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось загрузить файл")),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; is_final?: boolean; is_approved?: boolean }) =>
      updateAttachment(v.id, { is_final: v.is_final, is_approved: v.is_approved }),
    onSuccess: invalidate,
  });

  return (
    <div className="max-w-5xl space-y-4">
      <form
        className="flex flex-wrap items-end gap-2 rounded-xl border bg-card p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) create.mutate();
        }}
      >
        <select className={selectCls} value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(meta.attachment_kinds).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <Input
          placeholder="Подпись…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-48"
        />
        <Input
          placeholder="Ссылка (Яндекс.Диск / Битрикс / URL)…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-72"
        />
        <Button type="submit" variant="outline" disabled={!url.trim() || create.isPending}>
          Добавить ссылку
        </Button>
        <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input bg-transparent px-3 text-sm shadow-sm hover:bg-accent">
          <Upload className="mr-1.5 size-4" />
          {upload.isPending ? "Загрузка…" : "Загрузить файл"}
          <input
            type="file"
            className="hidden"
            accept="image/*,application/pdf"
            disabled={upload.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = "";
            }}
          />
        </label>
      </form>
      <p className="text-xs text-muted-foreground">
        Ссылка (папка исходников на Яндекс.Диске/Битриксе) или загруженный файл (фото/PDF).
        «Подпись» применяется и к ссылке, и к загружаемому файлу.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(list.data ?? []).map((a) => (
          <div key={a.id} className="overflow-hidden rounded-xl border bg-card transition-colors hover:border-foreground/20">
            <AttachmentMedia a={a} />
            <div className="space-y-1.5 p-2">
              <div className="flex items-start justify-between gap-1">
                <span
                  className="line-clamp-2 text-sm font-medium leading-tight"
                  title={a.title ?? ""}
                >
                  {a.title ?? "Без подписи"}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6 shrink-0"
                  onClick={() => del.mutate(a.id)}
                >
                  <Trash2 className="size-3.5 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{meta.attachment_kinds[a.kind]}</span>
                <button
                  type="button"
                  onClick={() => toggle.mutate({ id: a.id, is_final: !a.is_final })}
                  title="Отметить для техкарты"
                  className={
                    a.is_final
                      ? "inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-muted-foreground hover:bg-accent"
                  }
                >
                  <Star className={`size-3 ${a.is_final ? "fill-current" : ""}`} />
                  {a.is_final ? "Финал" : "в техкарту"}
                </button>
              </div>
            </div>
          </div>
        ))}
        {list.data?.length === 0 && (
          <EmptyState
            icon={<ImageOff className="size-7" />}
            title="Пока нет фото и макетов"
            hint="Добавьте ссылку на папку исходников (Яндекс.Диск/Битрикс) или загрузите фото/PDF — их можно отметить «Финал» для техкарты."
          />
        )}
      </div>
    </div>
  );
}

/** Дружелюбное пустое состояние: иконка + заголовок + подсказка. */
function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
}): React.ReactElement {
  return (
    <div className="col-span-full flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed py-10 text-center">
      <div className="text-muted-foreground/40">{icon}</div>
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="max-w-sm text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/** Медиа-превью вложения: внешняя ссылка (прямая картинка/линк) или загруженный
 * файл (картинка через AuthImage; прочее — открыть). */
function AttachmentMedia({ a }: { a: AttachmentView }): React.ReactElement {
  if (a.url) {
    return isImg(a.url) ? (
      <a href={a.url} target="_blank" rel="noreferrer" className="block bg-muted">
        <img src={a.url} alt={a.title ?? ""} className="h-36 w-full object-contain" />
      </a>
    ) : (
      <a
        href={a.url}
        target="_blank"
        rel="noreferrer"
        className="flex h-36 items-center justify-center bg-muted/40 text-sm text-muted-foreground hover:bg-muted"
      >
        <ExternalLink className="mr-1.5 size-4" /> Открыть ссылку
      </a>
    );
  }
  if (a.file_ref) {
    if (isImg(a.title)) {
      return (
        <div className="bg-muted">
          <AuthImage attId={a.id} className="h-36 w-full object-contain" alt={a.title ?? ""} />
        </div>
      );
    }
    return <FileOpen a={a} />;
  }
  return <div className="h-36 bg-muted/40" />;
}

function FileOpen({ a }: { a: AttachmentView }): React.ReactElement {
  const open = async (): Promise<void> => {
    try {
      const url = await fetchAttachmentBlobUrl(a.id);
      window.open(url, "_blank");
    } catch {
      toast.error("Не удалось открыть файл");
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      className="flex h-36 w-full flex-col items-center justify-center gap-1 bg-muted/40 text-xs text-muted-foreground hover:bg-muted"
    >
      <FileText className="size-7 opacity-60" />
      Открыть файл
    </button>
  );
}

// ── Заметки ──────────────────────────────────────────────────────────────────
export function NotesPanel({ projectId }: { projectId: string }): React.ReactElement {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["pdev", "notes", projectId],
    queryFn: () => listNotes(projectId),
  });
  const [body, setBody] = useState("");
  const add = useMutation({
    mutationFn: () => createNote(projectId, body.trim()),
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: ["pdev", "notes", projectId] });
    },
    onError: (e) => toast.error(apiErr(e, "Не удалось сохранить заметку")),
  });

  return (
    <div className="max-w-2xl space-y-3">
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) add.mutate();
        }}
      >
        <Textarea
          placeholder="Что происходит по продукту…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
        />
        <div>
          <Button type="submit" variant="outline" disabled={!body.trim() || add.isPending}>
            Добавить заметку
          </Button>
        </div>
      </form>
      <div className="space-y-2">
        {(list.data ?? []).map((n) => (
          <div key={n.id} className="rounded-xl border bg-card p-3">
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{n.author_username ?? "—"}</span>
              <span>{new Date(n.created_at).toLocaleString("ru-RU")}</span>
            </div>
            <div className="whitespace-pre-wrap text-sm">{n.body}</div>
          </div>
        ))}
        {list.data?.length === 0 && (
          <EmptyState
            icon={<StickyNote className="size-7" />}
            title="Заметок пока нет"
            hint="Фиксируйте, что решили по формуле, что ждёте от поставщика, какие правки по макету — чтобы контекст не терялся при передаче дел."
          />
        )}
      </div>
    </div>
  );
}

// ── Техкарта (печатный вид) ───────────────────────────────────────────────────
export function TechCardPanel({
  projectId,
  meta,
}: {
  projectId: string;
  meta: PdevMeta;
}): React.ReactElement {
  const tc = useQuery({
    queryKey: ["pdev", "techcard", projectId],
    queryFn: () => getTechCard(projectId),
  });
  if (tc.isLoading || !tc.data) return <div className="text-sm text-muted-foreground">Загрузка…</div>;
  const d = tc.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <div className="text-sm text-muted-foreground">
          Сводка для производства: состав, себестоимость, финальные фото.
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="mr-1 size-4" /> Печать / PDF
        </Button>
      </div>

      <div className="space-y-5 rounded-lg border bg-card p-5">
        <div>
          <h2 className="text-xl font-bold">{d.project.name}</h2>
          <div className="text-sm text-muted-foreground">
            {d.project.category ?? ""} · стадия: {meta.stages[d.project.stage]}
            {d.project.approved_at ? ` · утверждён ${fmtDate(d.project.approved_at)}` : ""}
          </div>
        </div>

        <section>
          <h3 className="mb-1 font-semibold">Себестоимость</h3>
          {d.rollup.roots.map((r) => (
            <div key={r.output_id} className="mb-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{r.display_name}</span>
                <span>{r.total_cost == null ? "—" : money(r.total_cost, r.currency)}</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-muted-foreground">
                    <th className="py-0.5 font-normal">Материал</th>
                    <th className="py-0.5 text-right font-normal">Кол-во</th>
                    <th className="py-0.5 text-right font-normal">Цена</th>
                  </tr>
                </thead>
                <tbody>
                  {r.lines.map((ln, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-0.5">{ln.material_name}</td>
                      <td className="whitespace-nowrap py-0.5 text-right tabular-nums text-muted-foreground">
                        {num(ln.qty_total)} {ln.unit}
                      </td>
                      <td className="whitespace-nowrap py-0.5 text-right tabular-nums">
                        {ln.has_price ? (
                          money(ln.line_cost, ln.currency ?? "RUB")
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400">нет цены</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>

        <section>
          <h3 className="mb-1 font-semibold">Дерево спецификации</h3>
          {d.tree.roots.map((r) => (
            <TechTree key={r.output_id} node={r} depth={0} />
          ))}
        </section>

        {d.final_photos.length > 0 && (
          <section>
            <h3 className="mb-2 font-semibold">Финальные фото</h3>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {d.final_photos.map((a) =>
                isImg(a.url) ? (
                  <img key={a.id} src={a.url!} alt={a.title ?? ""} className="h-28 w-full rounded object-cover" />
                ) : (
                  <a key={a.id} href={a.url!} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
                    {a.title ?? a.url}
                  </a>
                ),
              )}
            </div>
          </section>
        )}

      </div>
    </div>
  );
}

function TechTree({
  node,
  depth,
}: {
  node: import("@/api/product-dev").BomTreeNode;
  depth: number;
}): React.ReactElement {
  const name = node.display_name.replace(/\s*\([^)]*\)\s*$/, "");
  return (
    <div style={{ marginLeft: depth * 16 }} className="py-0.5 text-sm">
      <span className={`mr-1 rounded px-1.5 py-0.5 text-xs font-medium ${PEREDEL_STYLES[node.peredel] ?? ""}`}>
        {PEREDEL_LABEL[node.peredel] ?? node.peredel}
      </span>
      <span className="font-medium">{name}</span>
      {depth > 0 && <span className="ml-1 text-xs text-muted-foreground">× {num(node.qty_per_parent)}</span>}
      {node.lines.map((ln) =>
        ln.child ? (
          <TechTree key={ln.item_id} node={ln.child} depth={depth + 1} />
        ) : (
          <div key={ln.item_id} style={{ marginLeft: (depth + 1) * 16 }} className="text-muted-foreground">
            • {ln.ref_name} × {num(ln.qty)} {ln.unit}
          </div>
        ),
      )}
    </div>
  );
}
